/**
 * Redux slice for the product-grid2 app.
 *
 * This module manages the state for product grid displays, including:
 * - Product loading and storage
 * - Filter state (selected values and visibility)
 * - Sort method selection
 * - Product focus state (for hover interactions)
 * - Product option selections and quantity
 *
 * ## Browser History Integration
 *
 * This slice has special integration with browser history (via history.ts):
 * - Filter state changes are automatically pushed to the URL query string
 * - This allows users to bookmark, share, or use back/forward navigation with filters
 * - The `pushFilterStateChange` function is called as a side effect in filter reducers
 *
 * Note: While Redux best practices discourage side effects in reducers, this pattern
 * is intentional here for seamless browser integration. The history updates are
 * synchronous and don't affect the predictability of the state updates.
 *
 * @see history.ts for URL synchronization logic
 * @see persistence.ts for localStorage integration
 */
import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { castDraft } from "immer";

import { IOptionCode } from "../../models/catalogue";
import { IProductID } from "../../models/nominals";
import {
    ISetOptionValuePayload,
    setOptionValue as setOptionValueUtil,
} from "../reducer-utils";
import { defaults } from "./defaults";
import { pushFilterStateChange } from "./history";
import { IFilterState, IReduxState } from "./reducers.interfaces";
import { loadProducts } from "./thunks";
import { getInitialOptionValues } from "./utils";

const getFilterState = (
    filters: IReduxState["filters"],
    filterID: string,
): IFilterState => {
    return (
        filters[filterID] || {
            selectedValues: [],
            isOpen: true,
        }
    );
};

const mutateFilterState = (
    filters: IReduxState["filters"],
    filterID: string,
    mutateFn: (filter: IFilterState) => IFilterState,
) => {
    const clonedFilters = {
        ...filters,
    };
    const filterState = getFilterState(clonedFilters, filterID);
    clonedFilters[filterID] = mutateFn(filterState);
    return clonedFilters;
};

const productGrid2Slice = createSlice({
    name: "ProductGrid2",
    initialState: defaults,
    reducers: {
        /**
         * Applies filter state from browser history (back/forward navigation).
         * This is called by the history listener when users navigate with browser buttons.
         */
        applyHistoryUpdate: (
            state,
            action: PayloadAction<IReduxState["filters"]>,
        ) => {
            state.filters = castDraft(action.payload);
        },

        /**
         * Updates a single-select filter value (e.g., dropdown selection).
         * Triggers browser history update via pushFilterStateChange side effect.
         */
        updateSelectOneFilterValue: (
            state,
            action: PayloadAction<{
                optionNamespace: string;
                filterID: string;
                optionID: string | null;
            }>,
        ) => {
            const optionValues = {
                ...state.optionValues,
            };
            if (action.payload.optionID) {
                optionValues[action.payload.filterID as IOptionCode] =
                    action.payload.optionID;
            } else {
                delete optionValues[action.payload.filterID as IOptionCode];
            }
            const filter = getFilterState(
                state.filters,
                action.payload.filterID,
            );
            state.filters[action.payload.filterID] = {
                ...filter,
                selectedValues: action.payload.optionID
                    ? [action.payload.optionID]
                    : [],
            };
            state.optionValues = optionValues;
            pushFilterStateChange(state.filters);
        },

        /**
         * Updates a multi-select filter value (e.g., checkbox selection).
         * Can add or remove values from the filter's selected values set.
         * Triggers browser history update via pushFilterStateChange side effect.
         */
        updateSelectManyFilterValue: (
            state,
            action: PayloadAction<{
                optionNamespace: string;
                filterID: string;
                optionID: string;
                isSelected: boolean;
            }>,
        ) => {
            const optionValues = {
                ...state.optionValues,
            };
            if (action.payload.isSelected) {
                optionValues[action.payload.filterID as IOptionCode] =
                    action.payload.optionID;
            }
            const filter = getFilterState(
                state.filters,
                action.payload.filterID,
            );
            const selectedValues = new Set(filter.selectedValues);
            if (action.payload.isSelected) {
                selectedValues.add(action.payload.optionID);
            } else {
                selectedValues.delete(action.payload.optionID);
            }
            state.filters[action.payload.filterID] = {
                ...filter,
                selectedValues: [...selectedValues].sort(),
            };
            state.optionValues = optionValues;
            pushFilterStateChange(state.filters);
        },

        /**
         * Toggles the visibility (expanded/collapsed state) of a filter accordion.
         * Does not trigger browser history update since visibility is UI-only state.
         */
        updateFilterVisibility: (
            state,
            action: PayloadAction<{
                filterID: string;
                isOpen: boolean;
            }>,
        ) => {
            state.filters = castDraft(
                mutateFilterState(
                    state.filters,
                    action.payload.filterID,
                    (filter) => {
                        return {
                            ...filter,
                            isOpen: action.payload.isOpen,
                        };
                    },
                ),
            );
        },

        /**
         * Updates the currently selected sort method (e.g., price low to high).
         */
        updateSortMethod: (state, action: PayloadAction<string>) => {
            state.sortMethod = action.payload;
        },

        /**
         * Sets the currently focused product (for hover state and option selection).
         * Resets option values to the product's initial state.
         */
        focusProduct: (state, action: PayloadAction<IProductID>) => {
            const rootProduct = state.products.find(
                (p) => p.id === action.payload,
            );
            state.focusedProductID = action.payload;
            state.optionValues = rootProduct
                ? castDraft(getInitialOptionValues(rootProduct))
                : state.optionValues;
        },

        /**
         * Clears the focused product if it matches the provided ID.
         * Called when user stops hovering over a product card.
         */
        blurProduct: (state, action: PayloadAction<IProductID>) => {
            state.focusedProductID =
                action.payload === state.focusedProductID
                    ? null
                    : state.focusedProductID;
        },

        /**
         * Updates a product option value (e.g., size, color selection).
         * Used for product variant selection within product cards.
         */
        setOptionValue: (
            state,
            action: PayloadAction<ISetOptionValuePayload>,
        ) => {
            state.optionValues = castDraft(
                setOptionValueUtil(state.optionValues, action.payload),
            );
        },

        /**
         * Updates the selected quantity for add-to-cart actions.
         */
        setQuantity: (state, action: PayloadAction<number>) => {
            state.quantity = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            // Handle product loading started
            .addCase(loadProducts.pending, (state) => {
                state.isLoading = true;
                state.products = [];
            })
            // Handle product loading completed successfully
            .addCase(loadProducts.fulfilled, (state, action) => {
                state.isLoading = false;
                state.products = castDraft(action.payload);
            })
            // Handle product loading error
            .addCase(loadProducts.rejected, (state) => {
                state.isLoading = false;
                state.products = [];
            });
    },
});

export const {
    applyHistoryUpdate,
    updateSelectOneFilterValue,
    updateSelectManyFilterValue,
    updateFilterVisibility,
    updateSortMethod,
    focusProduct,
    blurProduct,
    setOptionValue,
    setQuantity,
} = productGrid2Slice.actions;

export default productGrid2Slice.reducer;
